import { starteAnpassendenTakt } from "./abfrage-takt.js";
import { gesteuert } from "./ruckel-schalter.js";
import { aufgabenSatz, lageGeaendert } from "./lage-fenster.js";
import { nachRuhe, schreibeScrollTop } from "./scroll-ruhe.js";
import { zeichen } from "./zeichen.js";
import {
  buildLoadingHint,
  buildPageHeader,
} from "./sheet-ui.js";
/**
 * voice-v8 — Background-Task-Manager (Pre.381)
 *
 * Zeigt alle laufenden + abgeschlossenen task_delegate-Calls.
 * Pollt /api/background-tasks alle 5s wenn mindestens ein "running"-
 * Task in der Liste war oder die View aktiv ist.
 *
 * Click auf einen "done"-Task → expandiert die Output-Preview inline.
 *
 * Plus Topbar/Nav-Badge: zeigt Anzahl laufender Tasks.
 */

interface TaskRecord {
  id: string;
  userId: number;
  tool: string;
  promptPreview: string;
  /** Der ungekuerzte Auftrag — fehlt bei Eintraegen von vor dem 30.08. */
  promptFull?: string | null;
  project: string | null;
  model: string;
  status: "running" | "done" | "failed";
  startedAt: number;
  finishedAt: number | null;
  elapsedMs: number | null;
  outputPreview: string | null;
  error: string | null;
  // Pre.388: Live-Status + Files
  lastStatusLine?: string | null;
  workdir?: string | null;
  filesJson?: string | null;
  // Pre.445: Prozess-Steps-Log (JSON-Array von {ts,line})
  stepsJson?: string | null;
}

interface TaskStep {
  ts: number;
  line: string;
}

interface TaskFile {
  name: string;
  size: number;
  mtime: number;
}

/**
 * Pre.488: Formatiert Task-Zeitstempel relativ-friendly.
 * - heute  → "13:42"
 * - gestern → "gestern 13:42"
 * - älter  → "23.05. 13:42"
 */
function formatTaskTime(ts: number): string {
  const d = new Date(ts);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const hhmm = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  if (sameDay) return hhmm;
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (
    d.getFullYear() === yesterday.getFullYear() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getDate() === yesterday.getDate()
  ) {
    return `gestern ${hhmm}`;
  }
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}. ${hhmm}`;
}

type TaskFilter = "all" | "running" | "done" | "failed";

export class TasksView {
  private root: HTMLElement;
  private list: HTMLDivElement;
  private hint: HTMLParagraphElement;
  private pollTimer: number | null = null;
  private currentTasks: TaskRecord[] = [];

  constructor(root: HTMLElement) {
    this.root = root;
    this.root.replaceChildren();
    this.root.className = "view tasks-view";

    // 2026-07-23 (Martin, "eine Wahrheit"): Norm-Kopf statt Eigenbau —
    // buildPageHeader wie auf allen anderen Seiten, Reload im Aktions-Slot.
    const refresh = document.createElement("button");
    refresh.type = "button";
    refresh.className = "tasks-view-refresh";
    refresh.textContent = "Aktualisieren";
    refresh.title = "Aktualisieren";
    refresh.setAttribute("aria-label", "Aktualisieren");
    refresh.addEventListener("click", () => void this.refresh());
    this.root.appendChild(
      // 2026-08-20: Aktualisieren ist "neu laden", nicht "etwas anlegen" —
      // gehoert ins ⋯-Menue statt in die Titelzeile (dieselbe Regel wie bei
      // Essenz-Spiegel, Human Design und Mein Leben).
      buildPageHeader("Hintergrund-Tasks", () => {}, {
        menuItems: [
          { label: "Aktualisieren", onSelect: () => refresh.click() },
        ],
      }),
    );

    this.hint = document.createElement("p");
    this.hint.className = "tasks-view-hint";
    this.hint.textContent = "Lade…";
    this.root.appendChild(this.hint);

    this.list = document.createElement("div");
    this.list.className = "tasks-view-list";
    this.root.appendChild(this.list);

    void this.refresh();
    this.startPolling();
  }

  destroy(): void {
    this.stopPolling();
  }

  private startPolling(): void {
    if (this.pollTimer !== null) return;
    this.pollTimer = window.setInterval(gesteuert("live", () => void this.refresh(), true), 5000);
  }

  private stopPolling(): void {
    if (this.pollTimer !== null) {
      window.clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  // #130: Signatur des zuletzt gerenderten Stands. Der 5s-Poll baute die
  // Liste IMMER neu auf (replaceChildren) → Scroll sprang auf Anfang, während
  // Martin ein langes Ergebnis las. Hat sich nichts Sichtbares geändert,
  // überspringen wir den Neuaufbau ganz.
  private lastRenderSig = "";

  private renderSignature(tasks: TaskRecord[]): string {
    return tasks
      .map((t) => {
        // Laufende Tasks: Sekunden-Tick rein, damit die Live-Zeit weiterläuft.
        // Fertige/fehlgeschlagene: stabile Felder → Signatur ändert sich nicht
        // mehr → kein Neuaufbau mehr, Scroll bleibt wo der User liest.
        // pre.851: KEIN Sekunden-Tick mehr in der Signatur — der zwang bei
        // jedem laufenden Task alle 5s einen Komplett-Neuaufbau (Scroll-Sprung).
        // Jetzt nur die echten Inhalts-Felder: Liste baut sich neu auf, wenn die
        // CLI wirklich etwas tut (Status-Zeile/Schritte), nicht im Sekundentakt.
        const live =
          t.status === "running"
            ? `${t.lastStatusLine ?? ""}|${t.stepsJson ?? ""}`
            : "";
        return `${t.id}:${t.status}:${live}:${(t.outputPreview ?? "").length}:${t.error ?? ""}:${t.filesJson ?? ""}`;
      })
      .join("~");
  }

  private async refresh(): Promise<void> {
    if (this.list && !this.list.hasChildNodes()) {
      this.list.replaceChildren(buildLoadingHint("Lade Aufgaben \u2026"));
    }
    try {
      const res = await fetch("/api/background-tasks", {
        credentials: "same-origin",
      });
      if (!res.ok) {
        this.hint.textContent = "Server-Fehler beim Laden.";
        return;
      }
      const data = (await res.json()) as { tasks?: TaskRecord[] };
      this.currentTasks = data.tasks ?? [];
      // #130: nur neu rendern, wenn sich am sichtbaren Stand etwas geändert hat.
      const sig = `${this.filter}|${this.renderSignature(this.currentTasks)}`;
      if (sig !== this.lastRenderSig) {
        this.lastRenderSig = sig;
        // Abfrage im Hintergrund: der Neuaufbau setzt die Liste zurück und schreibt `scrollTop` —
        // nie unter dem Finger oder in ein Nachlaufen hinein (lib/scroll-ruhe.ts, pre.3607).
        nachRuhe(this.list, this, () => this.render(), "aufgaben");
      }
      updateNavBadge(this.currentTasks);
    } catch {
      this.hint.textContent = "Verbindung verloren.";
    }
  }

  // Pre.815: Archiv-Filter (Alle / Läuft / Fertig / Fehler).
  private filter: TaskFilter = "all";

  private render(): void {
    // #130: Scroll-Position über den Neuaufbau hinweg erhalten. replaceChildren
    // setzt den Scroll-Container sonst auf 0 — der User wird beim Lesen eines
    // langen Ergebnisses an den Anfang gerissen. Nach dem synchronen Neuaufbau
    // ist die Höhe identisch, also landet scrollTop wieder an derselben Stelle.
    // pre.851: Scroll-Stand auf der LISTE sichern (dem echten Scroll-Container).
    // Der pre.842-Fix zielte auf this.root (.view, overflow:hidden → scrollTop
    // immer 0) → Restore war ein No-op und replaceChildren riss auf 0 = Sprung
    // nach oben, während Martin ein langes Ergebnis las.
    const prevScroll = this.list.scrollTop;
    const restoreScroll = (): void => {
      schreibeScrollTop(this.list, prevScroll, "aufgaben");
    };
    if (this.currentTasks.length === 0) {
      this.hint.textContent =
        "Noch keine Hintergrund-Tasks. Beauftrage den Bot z.B. mit „Delegier an Claude CLI: schreib mir ein Script…“, dann erscheint hier der Fortschritt.";
      this.list.replaceChildren();
      return;
    }
    const running = this.currentTasks.filter((t) => t.status === "running");
    const done = this.currentTasks.filter((t) => t.status === "done");
    const failed = this.currentTasks.filter((t) => t.status === "failed");
    // 2026-08-26 (Norm B6/B9): Hier standen dieselben drei Zahlen, die drei
    // Zeilen tiefer schon auf den Filter-Chips stehen ("Alle 3 · Fertig 1 ·
    // Fehler 2") — dreimal dasselbe, bevor die erste Aufgabe kommt.
    //
    // ⚠️ Ersatzlos streichen ginge nicht: die Hinweiszeile trug EINE Angabe,
    // die sonst nirgends steht — dass das Archiv nach 30 Tagen endet. Sie sagt
    // jetzt nur noch das, und bei laufenden Aufgaben, was gerade passiert.
    this.hint.textContent =
      running.length > 0
        ? `${running.length} läuft gerade`
        : "Archiv der letzten 30 Tage";

    this.list.replaceChildren();

    // Pre.815: Filter-Chips — Archiv-Übersicht nach Status.
    const chips = document.createElement("div");
    chips.className = "task-filter-chips";
    const defs: Array<{ id: TaskFilter; label: string; n: number }> = [
      { id: "all", label: "Alle", n: this.currentTasks.length },
      { id: "running", label: "Läuft", n: running.length },
      { id: "done", label: "Fertig", n: done.length },
      { id: "failed", label: "Fehler", n: failed.length },
    ];
    for (const d of defs) {
      if (d.id !== "all" && d.n === 0) continue;
      const chip = document.createElement("button");
      chip.type = "button";
      chip.className = "task-filter-chip";
      chip.dataset.active = String(this.filter === d.id);
      chip.textContent = `${d.label} (${d.n})`;
      chip.addEventListener("click", () => {
        this.filter = d.id;
        this.render();
      });
      chips.appendChild(chip);
    }
    this.list.appendChild(chips);

    // #185: Pipeline-Sicht — bei "Alle" zwei klar getrennte Abschnitte:
    // „Läuft jetzt" (parallel arbeitende Tasks) und „Archiv" (fertig/Fehler).
    // Bei gesetztem Status-Filter bleibt die flache Liste.
    if (this.filter === "all") {
      const runningTasks = this.currentTasks.filter(
        (t) => t.status === "running",
      );
      const archivedTasks = this.currentTasks.filter(
        (t) => t.status !== "running",
      );
      if (runningTasks.length > 0) {
        // Ehrlich: laufende Tasks arbeiten GLEICHZEITIG (keine serielle Queue).
        const note =
          runningTasks.length > 1
            ? `${runningTasks.length} Tasks arbeiten gleichzeitig.`
            : null;
        this.list.appendChild(
          this.makeSectionHead(`Läuft jetzt (${runningTasks.length})`, note),
        );
        for (const t of runningTasks) this.list.appendChild(this.makeRow(t));
      }
      if (archivedTasks.length > 0) {
        this.list.appendChild(
          this.makeSectionHead(`Archiv (${archivedTasks.length})`, null),
        );
        for (const t of archivedTasks) this.list.appendChild(this.makeRow(t));
      }
    } else {
      const visible = this.currentTasks.filter((t) => t.status === this.filter);
      for (const t of visible) this.list.appendChild(this.makeRow(t));
    }
    // #130: Scroll-Stand nach dem Neuaufbau wiederherstellen.
    restoreScroll();
  }

  // #185: Abschnitts-Kopf der Pipeline-Sicht (DRY — von beiden Gruppen genutzt).
  private makeSectionHead(label: string, note: string | null): HTMLElement {
    const head = document.createElement("div");
    head.className = "task-section-head";
    const title = document.createElement("span");
    title.className = "task-section-title";
    title.textContent = label;
    head.appendChild(title);
    if (note) {
      const noteEl = document.createElement("span");
      noteEl.className = "task-section-note";
      noteEl.textContent = note;
      head.appendChild(noteEl);
    }
    return head;
  }

  private makeRow(t: TaskRecord): HTMLElement {
    const row = document.createElement("article");
    row.className = `task-row task-row--${t.status}`;

    const top = document.createElement("div");
    top.className = "task-row-top";
    const status = document.createElement("span");
    status.className = "task-row-status";
    // 2026-08-26 (Norm A): standen als ● (U+25CF) und ✕ (U+2715) im Text —
    // durch text-transform: uppercase wurden sie mitgerendert und sahen auf
    // jedem Geraet anders aus. Der Zustand steht jetzt im Wort, die Farbe
    // kommt aus .task-row--running / --failed.
    status.textContent =
      t.status === "running"
        ? "läuft"
        : t.status === "done"
          ? "fertig"
          : "fehlgeschlagen";
    top.appendChild(status);
    if (t.project) {
      const proj = document.createElement("span");
      proj.className = "task-row-project";
      proj.textContent = t.project;
      top.appendChild(proj);
    }
    const model = document.createElement("span");
    model.className = "task-row-model";
    model.textContent = t.model;
    top.appendChild(model);
    // 2026-08-26 (Norm B8, "hoechstens zwei Zeitangaben-Formen und nie beide
    // in derselben Zeile"): Hier standen die Dauer ("739s") und der Zeitpunkt
    // ("22.08. 19:54") nebeneinander — zwei Formen, die beide "Zeit" sagen und
    // sich beim Ueberfliegen gegenseitig verwischen.
    //
    // Bei einer LAUFENDEN Aufgabe zaehlt die Dauer (wie lange schon?), bei einer
    // abgeschlossenen der Zeitpunkt (wann war das?). Die Dauer einer fertigen
    // Aufgabe wandert an den Zeitpunkt als Erklaerung beim Verweilen.
    const time = document.createElement("span");
    time.className = "task-row-time";
    if (t.status === "running") {
      time.textContent = `${Math.round((Date.now() - t.startedAt) / 1000)}s …`;
      top.appendChild(time);
    }
    // Pre.488: Datum + Uhrzeit anzeigen, damit User sieht WANN ein Task lief.
    // "heute" → nur HH:MM, "gestern" → "gestern HH:MM", älter → "DD.MM. HH:MM"
    const startedAtEl = document.createElement("span");
    startedAtEl.className = "task-row-started";
    startedAtEl.textContent = formatTaskTime(t.startedAt);
    const dauer = Math.round((t.elapsedMs ?? 0) / 1000);
    startedAtEl.title =
      t.status === "running" || !dauer
        ? new Date(t.startedAt).toLocaleString("de-AT")
        : `${new Date(t.startedAt).toLocaleString("de-AT")} · ${dauer}s gelaufen`;
    top.appendChild(startedAtEl);
    // pre.855 (Martins Wunsch — abbrechbar): laufende Tasks lassen sich
    // hier hart stoppen.
    if (t.status === "running") {
      const cancel = document.createElement("button");
      cancel.type = "button";
      cancel.className = "task-row-cancel";
      cancel.textContent = "Abbrechen";
      cancel.addEventListener("click", async () => {
        cancel.disabled = true;
        cancel.textContent = "Breche ab …";
        try {
          const r = await fetch(
            `/api/background-tasks/${encodeURIComponent(t.id)}/cancel`,
            { method: "POST", credentials: "same-origin" },
          );
          if (r.ok) {
            window.dispatchEvent(
              new CustomEvent("voice-v8:toast", {
                detail: { message: "Task abgebrochen.", kind: "info" },
              }),
            );
            void this.refresh();
          } else {
            cancel.disabled = false;
            cancel.textContent = "Abbrechen";
          }
        } catch {
          cancel.disabled = false;
          cancel.textContent = "Abbrechen";
        }
      });
      top.appendChild(cancel);
    }
    /*
     * „Nochmal starten" — 2026-08-30.
     *
     * Martins zweiter CLI-Auftrag (19.08.) endete mit *„Bitte den Task
     * NOCHMAL anfordern, dann laeuft frische Session."* Die Sitzung war
     * abgelaufen; der Server hat sie zurueckgesetzt, ein zweiter Anlauf
     * haette funktioniert. Nur: Martin haette den ganzen Auftrag neu
     * formulieren muessen — es war eine mehrzeilige Recherche-Anweisung.
     *
     * ⚠️ Bis heute war das gar nicht anders moeglich: gespeichert waren nur
     * die ersten 200 Zeichen (`prompt_preview`). Seit dem 30.08. liegt der
     * volle Text in `prompt_full`; erst dadurch gibt es hier etwas zu
     * wiederholen.
     *
     * Der Knopf FUELLT das Eingabefeld und sendet NICHT von selbst: ein
     * CLI-Auftrag laeuft Minuten und kostet Kontingent — den letzten
     * Handgriff behaelt Martin. Dafuer wird das vorhandene Ereignis
     * `voice-v8:example-prompt` benutzt statt ein zweites daneben zu bauen.
     */
    const volltext = (t.promptFull ?? "").trim();
    if (t.status === "failed" && volltext) {
      const nochmal = document.createElement("button");
      nochmal.type = "button";
      nochmal.className = "task-row-cancel";
      nochmal.textContent = "Nochmal starten";
      nochmal.title = "Setzt den ursprünglichen Auftrag ins Eingabefeld";
      nochmal.addEventListener("click", () => {
        window.dispatchEvent(
          new CustomEvent("voice-v8:example-prompt", {
            detail: { prompt: volltext },
          }),
        );
      });
      top.appendChild(nochmal);
    }
    row.appendChild(top);

    const prompt = document.createElement("div");
    prompt.className = "task-row-prompt";
    prompt.textContent = t.promptPreview;
    row.appendChild(prompt);

    // Pre.388: Live-Status während running ("Tool: Write" etc.)
    if (t.status === "running" && t.lastStatusLine) {
      const live = document.createElement("div");
      live.className = "task-row-live";
      live.textContent = t.lastStatusLine;
      row.appendChild(live);
    }

    // Pre.445: Prozess-Steps-Log (default offen während running)
    if (t.stepsJson) {
      try {
        const steps = JSON.parse(t.stepsJson) as TaskStep[];
        if (steps.length > 0) {
          const stepsDet = document.createElement("details");
          stepsDet.className = "task-row-steps";
          stepsDet.open = t.status === "running";
          const sum = document.createElement("summary");
          sum.textContent = `Prozess-Schritte (${steps.length})`;
          stepsDet.appendChild(sum);
          const ul = document.createElement("ul");
          ul.className = "task-row-steps-list";
          for (const s of steps.slice(-30)) {
            const li = document.createElement("li");
            const tEl = document.createElement("span");
            tEl.className = "task-row-step-ts";
            const d = new Date(s.ts);
            tEl.textContent = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}:${String(d.getSeconds()).padStart(2, "0")}`;
            li.appendChild(tEl);
            const lEl = document.createElement("span");
            lEl.className = "task-row-step-line";
            lEl.textContent = s.line;
            li.appendChild(lEl);
            ul.appendChild(li);
          }
          stepsDet.appendChild(ul);
          row.appendChild(stepsDet);
        }
      } catch {
        /* invalid json */
      }
    }

    // Pre.388: Files-Listing nach Done
    if (t.filesJson) {
      try {
        const files = JSON.parse(t.filesJson) as TaskFile[];
        if (files.length > 0) {
          const filesBlock = document.createElement("div");
          filesBlock.className = "task-row-files";
          const fHead = document.createElement("div");
          fHead.className = "task-row-files-head";
          fHead.textContent = `Erzeugte Dateien (${files.length})`;
          filesBlock.appendChild(fHead);
          for (const f of files) {
            const fItem = document.createElement("div");
            fItem.className = "task-row-file";
            const fName = document.createElement("span");
            fName.className = "task-row-file-name";
            fName.textContent = f.name;
            const fSize = document.createElement("span");
            fSize.className = "task-row-file-size";
            fSize.textContent = formatSize(f.size);
            fItem.appendChild(fName);
            fItem.appendChild(fSize);
            // Pre.392: ▶ Ausführen-Button für executable extensions
            const ext = f.name.includes(".")
              ? f.name.split(".").pop()!.toLowerCase()
              : "";
            if (["py", "js", "mjs", "sh", "bash"].includes(ext) && t.workdir) {
              const runBtn = document.createElement("button");
              runBtn.type = "button";
              runBtn.className = "task-row-file-run";
              runBtn.title = `Ausführen mit ${ext === "py" ? "python3" : ext === "sh" || ext === "bash" ? "bash" : "node"}`;
              // 2026-08-26 (Norm A): war der Dingbat ▶ (U+25B6).
              runBtn.innerHTML = zeichen("abspielen", 14);
              runBtn.addEventListener("click", (ev) => {
                ev.stopPropagation();
                void runFile(t.workdir!, f.name, fItem);
              });
              fItem.appendChild(runBtn);
            }
            filesBlock.appendChild(fItem);
          }
          if (t.workdir) {
            const fPath = document.createElement("div");
            fPath.className = "task-row-files-path";
            fPath.textContent = t.workdir;
            filesBlock.appendChild(fPath);
          }
          row.appendChild(filesBlock);
        }
      } catch {
        /* invalid json */
      }
    }

    if (t.outputPreview) {
      // Pre.445: default-open damit User Output sofort sieht ohne Klick
      const details = document.createElement("details");
      details.className = "task-row-output";
      details.open = true;
      const summary = document.createElement("summary");
      summary.textContent = "Ergebnis";
      details.appendChild(summary);
      const pre = document.createElement("pre");
      pre.textContent = t.outputPreview;
      details.appendChild(pre);
      row.appendChild(details);
    }
    if (t.error) {
      const err = document.createElement("div");
      err.className = "task-row-error";
      err.textContent = `Fehler: ${t.error}`;
      row.appendChild(err);
    }

    return row;
  }
}

/**
 * Lightweight nav-badge + topbar-pulse sync.
 * Pre.382: zusätzlich blinkender Topbar-Pulse mit Anzahl. Click öffnet
 * die Tasks-View.
 */
/** Letzter Stand der Aufgaben — entscheidet ueber den Abfragetakt. */
let letzterAufgabenStand = "";

function updateNavBadge(tasks: TaskRecord[]): void {
  const running = tasks.filter((t) => t.status === "running").length;
  const badge = document.getElementById("nav-tasks-badge");
  if (badge) {
    if (running > 0) {
      badge.textContent = String(running);
      badge.hidden = false;
    } else {
      badge.hidden = true;
    }
  }
  const pulse = document.getElementById(
    "topbar-tasks-pulse",
  ) as HTMLButtonElement | null;
  // 11.10.2026: der Puls steht nicht mehr im Kopf, sondern als Zeile in der Lage unter der
  // Glocke („2 Aufgaben im Hintergrund“); die Tafel und die Glocke erfahren es über lageGeaendert().
  const text = document.getElementById("topbar-tasks-text");
  if (pulse && text) {
    if (running > 0) text.textContent = aufgabenSatz(running);
    pulse.hidden = running <= 0;
    lageGeaendert();
  }
}

// Pre.392: Code-File ausführen via /api/exec-file. Output landet als
// ausklappbarer Block direkt unter der File-Row.
async function runFile(
  workdir: string,
  filename: string,
  parentEl: HTMLElement,
): Promise<void> {
  // Vorhandenes Output-Block entfernen falls da
  parentEl.parentElement
    ?.querySelectorAll(`.task-file-output[data-file="${filename}"]`)
    .forEach((e) => e.remove());
  const out = document.createElement("div");
  out.className = "task-file-output";
  out.dataset.file = filename;
  out.textContent = "Läuft …";
  parentEl.insertAdjacentElement("afterend", out);
  try {
    const res = await fetch("/api/exec-file", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ workdir, filename }),
    });
    const data = (await res.json()) as {
      ok?: boolean;
      stdout?: string;
      stderr?: string;
      exitCode?: number;
      error?: string;
    };
    out.replaceChildren();
    const head = document.createElement("div");
    head.className = "task-file-output-head";
    head.textContent = data.ok
      ? `exit ${data.exitCode}`
      : // 2026-08-26 (Norm A): war "✕ …". Der Zustand steht im Wort,
        // die Farbe kommt aus .task-row--failed.
        `${data.error ?? `exit ${data.exitCode}`}`;
    out.appendChild(head);
    if (data.stdout) {
      const pre = document.createElement("pre");
      pre.textContent = data.stdout;
      out.appendChild(pre);
    }
    if (data.stderr) {
      const errEl = document.createElement("pre");
      errEl.className = "task-file-output-err";
      errEl.textContent = data.stderr;
      out.appendChild(errEl);
    }
  } catch (e) {
    out.textContent = `Fehler: ${e instanceof Error ? e.message : String(e)}`;
  }
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

/** Globale Polling-Schleife für die Nav-Badge — alle 15s. */
export function startGlobalTasksPolling(): void {
  const poll = async (): Promise<boolean> => {
    try {
      const res = await fetch("/api/background-tasks", {
        credentials: "same-origin",
      });
      if (!res.ok) return false;
      const data = (await res.json()) as { tasks?: TaskRecord[] };
      const tasks = data.tasks ?? [];
      updateNavBadge(tasks);
      // 2026-07-31: Meldet, ob sich die Lage geaendert hat — der Takt
      // richtet sich danach. Laufende Aufgaben halten ihn schnell.
      const stand = `${tasks.length}:${tasks.filter((t) => t.status === "running").length}`;
      const geaendert = stand !== letzterAufgabenStand;
      letzterAufgabenStand = stand;
      return geaendert;
    } catch {
      return false;
    }
  };
  /**
   * 2026-07-31 (Martin: "mein Akku ist immer extrem schnell leer"):
   *
   * Hier stand ein starrer 15-Sekunden-Takt — gemessen 344 Anfragen pro
   * Stunde, nur damit eine Zahl an einem Symbol stimmt. Jetzt schnell,
   * solange sich etwas tut, danach in Schritten langsamer bis zwei Minuten.
   * Startet eine Hintergrund-Aufgabe, faellt der Takt beim naechsten Blick
   * sofort zurueck.
   */
  starteAnpassendenTakt({
    abfrage: poll,
    schnellMs: 15_000,
    langsamMs: 120_000,
    name: "aufgaben-zaehler",
  });
}
